import './BudgetCard.scss';
import { ReactElement, useMemo, useState } from 'react';
import { MdOutlineFileDownload } from 'react-icons/md';
import { Appearance, Placement, Size } from '../../../../Components/designConstants';
import { Dropdown } from '../../../../Components/Dropdown';
import { IconButton } from '../../../../Components/IconButton';
import { downloadPNG } from '../../../../utils/download-utils';
import { BudgetBuilderGroup, BudgetOutcome, CurvePoint } from '../budgetBuilderContract';
import {
  formatEfficiencyCompact,
  formatEfficiencyValue,
  formatOutcomeValue,
  formatSpendValue,
  outcomeLabel
} from '../budgetBuilderFormat';
import { nearestIndexBy, rangeOf } from '../../curveSliderMath';
import {
  clampIndex,
  derivedEfficiency,
  marginalEfficiency,
  pickSeries
} from '../budgetBuilderMath';
import { LinkedSlider } from '../../components/LinkedSlider';
import { SaturationCurveChart } from '../../components/SaturationCurveChart';

type PlotMetric = 'outcome' | 'efficiency' | 'marginal';

// Shared label for the selected point sitting exactly on the primary budget.
const AT_PRIMARY = 'at primary';

const toBand = (lower: number | null, upper: number | null): [number, number] | undefined =>
  lower != null && upper != null ? [Math.min(lower, upper), Math.max(lower, upper)] : undefined;

const safeId = (s: string): string => s.replace(/[^a-zA-Z0-9]+/g, '-');

interface BudgetCardProps {
  group: BudgetBuilderGroup;
  /** Page-level Volume/Revenue selection; the card shows that series if it has it. */
  selectedOutcome: BudgetOutcome;
  /** Weeks in the forecast period; converts the weekly curve to period totals for the BAU readout. */
  weeks?: number;
  /** Selected point index, owned by the page so the table can reflect the same budget. */
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
}

/**
 * One spend-group (channel × platform × tactic, at whatever grain the forecast
 * ran): three interlinked sliders (spend / outcome / efficiency) bound to a
 * draggable point on the fitted saturation curve. Moving any control moves the
 * shared point; each slider is positioned by its own value.
 */
export const BudgetCard = ({
  group,
  selectedOutcome,
  weeks,
  activeIndex: activeIndexProp,
  onActiveIndexChange
}: BudgetCardProps): ReactElement => {
  const series = useMemo(() => pickSeries(group, selectedOutcome), [group, selectedOutcome]);
  const { curve, outcome, efficiencyMetric } = series;
  const maxIndex = curve.length - 1;
  const activeIndex = clampIndex(activeIndexProp, maxIndex);
  const point = curve[activeIndex];
  const setIndex = (i: number): void => onActiveIndexChange(clampIndex(i, maxIndex));

  const [plotMetric, setPlotMetric] = useState<PlotMetric>('outcome');

  const effOf = useMemo(
    () => (p: CurvePoint) => derivedEfficiency(efficiencyMetric, p),
    [efficiencyMetric]
  );
  const activeEff = effOf(point);

  const [spendMin, spendMax] = useMemo(() => rangeOf(curve, p => p.spend), [curve]);
  const [volMin, volMax] = useMemo(() => rangeOf(curve, p => p.volume), [curve]);
  const [effMin, effMax] = useMemo(() => rangeOf(curve, p => effOf(p).value), [curve, effOf]);

  // Marginal = the curve's slope as marginal ROAS, or its reciprocal as marginal CPA
  // ("what the next $1 returns" / "what the next conversion costs"); no CI band.
  const chartPoints = useMemo(
    () =>
      curve.map((p, i) => {
        if (plotMetric === 'marginal') {
          return {
            spend: p.spend,
            value: marginalEfficiency(efficiencyMetric, curve, i),
            band: undefined
          };
        }
        if (plotMetric === 'efficiency') {
          const e = effOf(p);
          return { spend: p.spend, value: e.value, band: toBand(e.lower, e.upper) };
        }
        return { spend: p.spend, value: p.volume, band: toBand(p.volumeLower, p.volumeUpper) };
      }),
    [curve, plotMetric, effOf, efficiencyMetric]
  );

  const outcomeName = outcomeLabel(outcome, group.kpi);
  const marginalName = `Marginal ${efficiencyMetric}`;
  const plotOptions = [
    `Spend vs Weekly ${outcomeName}`,
    `Spend vs Weekly ${efficiencyMetric}`,
    `Spend vs ${marginalName}`
  ];
  const optionForMetric: Record<PlotMetric, string> = {
    outcome: plotOptions[0],
    efficiency: plotOptions[1],
    marginal: plotOptions[2]
  };
  const metricForOption = (v: string): PlotMetric => {
    if (v === plotOptions[1]) {
      return 'efficiency';
    }
    return v === plotOptions[2] ? 'marginal' : 'outcome';
  };

  // Formatters/label by plot: outcome uses outcome units; efficiency & marginal
  // both read as a ROAS (×) / CPA ($) rate.
  const yLabelByMetric: Record<PlotMetric, string> = {
    outcome: `Weekly ${outcomeName}`,
    efficiency: `Weekly ${efficiencyMetric}`,
    marginal: marginalName
  };
  const yLabel = yLabelByMetric[plotMetric];
  const yFormatter =
    plotMetric === 'outcome'
      ? (v: number | null) => formatOutcomeValue(outcome, v)
      : (v: number | null) => formatEfficiencyValue(efficiencyMetric, v);
  const yTickFormatter =
    plotMetric === 'outcome'
      ? (v: number) => formatOutcomeValue(outcome, v)
      : (v: number) => formatEfficiencyCompact(efficiencyMetric, v);
  // Break-even (y = 1) is only meaningful for marginal ROAS (revenue).
  const showBreakeven = plotMetric === 'marginal' && efficiencyMetric === 'ROAS';
  const title = [group.channel, group.platform].filter(Boolean).join(', ');
  const subtitle = [group.tactic, group.kpi].filter(Boolean).join(' · ');
  const chartId = `bb-chart-${safeId(group.id)}`;
  const pngName = safeId(`budget_${title}_${group.tactic}_${efficiencyMetric}`).replace(/-/g, '_');

  const ciText = (lower: number | null, upper: number | null, fmt: (v: number | null) => string) =>
    lower != null && upper != null
      ? `${fmt(Math.min(lower, upper))} – ${fmt(Math.max(lower, upper))}`
      : undefined;

  // Primary budget as a weekly spend — sits directly on the weekly curve. Shown
  // weekly to match the rest of the view (all figures are weekly), with the
  // period total (weekly × weeks) in parens for context; the selected scenario
  // is likewise weekly with its % vs primary.
  const primaryWeekly =
    group.primarySpend != null && group.primarySpend > 0 ? group.primarySpend : undefined;
  const primaryText = useMemo(() => {
    if (primaryWeekly == null) {
      return undefined;
    }
    const pct = Math.round(((point.spend - primaryWeekly) / primaryWeekly) * 100);
    const vsPrimary = pct === 0 ? AT_PRIMARY : `${pct > 0 ? '+' : ''}${pct}% vs primary`;
    const selectedWeekly =
      pct === 0 ? AT_PRIMARY : `${formatSpendValue(point.spend)}/wk (${vsPrimary})`;
    if (!weeks) {
      return `Primary: ${formatSpendValue(primaryWeekly)}/wk · selected ${selectedWeekly}`;
    }
    // Label the parenthetical as the exact period total (weekly × days/7)
    // rather than showing a week count: a user-picked date range rarely lands
    // on whole weeks, so a rounded count ("4 wks") wouldn't tie out against the
    // total and a fractional one ("3.57 wks") reads as strange. The forecast
    // date range is shown at the page level, so it carries the period context.
    return `Primary: ${formatSpendValue(primaryWeekly)}/wk (${formatSpendValue(
      primaryWeekly * weeks
    )} total) · selected ${selectedWeekly}`;
  }, [primaryWeekly, weeks, point.spend]);

  return (
    <div className='bb-card' data-testid='budget-card'>
      <div className='bb-card-head'>
        <div className='bb-card-titles'>
          <span className='bb-card-title'>{title}</span>
          <span className='bb-card-subtitle'>{subtitle}</span>
          {primaryText && <span className='bb-card-budget'>{primaryText}</span>}
        </div>
      </div>

      <div className='bb-card-sliders'>
        <LinkedSlider
          label='Weekly Spend'
          value={point.spend}
          min={spendMin}
          max={spendMax}
          valueText={formatSpendValue(point.spend)}
          onValueChange={v => setIndex(nearestIndexBy(curve, p => p.spend, v))}
        />
        <LinkedSlider
          label={`Weekly ${outcomeName}`}
          value={point.volume}
          min={volMin}
          max={volMax}
          valueText={formatOutcomeValue(outcome, point.volume)}
          ciText={ciText(point.volumeLower, point.volumeUpper, v => formatOutcomeValue(outcome, v))}
          onValueChange={v => setIndex(nearestIndexBy(curve, p => p.volume, v))}
        />
        <LinkedSlider
          label={`Weekly ${efficiencyMetric}`}
          value={activeEff.value}
          min={effMin}
          max={effMax}
          valueText={formatEfficiencyValue(efficiencyMetric, activeEff.value)}
          ciText={ciText(activeEff.lower, activeEff.upper, v =>
            formatEfficiencyValue(efficiencyMetric, v)
          )}
          onValueChange={v => setIndex(nearestIndexBy(curve, p => effOf(p).value, v))}
        />
      </div>

      <div className='bb-card-plot'>
        <div className='bb-card-plot-head'>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            ariaLabel='Plot metric'
            value={optionForMetric[plotMetric]}
            options={plotOptions}
            onChange={(v: string) => setPlotMetric(metricForOption(v))}
          />
          <IconButton
            appearance={Appearance.OUTLINED}
            onClick={() => downloadPNG(`#${chartId}`, pngName)}
            title='Download plot as PNG'
          >
            <MdOutlineFileDownload />
          </IconButton>
        </div>
        <div id={chartId} className='bb-card-chart'>
          <SaturationCurveChart
            points={chartPoints}
            activeIndex={activeIndex}
            onSelectIndex={setIndex}
            primaryX={primaryWeekly}
            showBreakeven={showBreakeven}
            xFormatter={formatSpendValue}
            yLabel={yLabel}
            yFormatter={yFormatter}
            yTickFormatter={yTickFormatter}
          />
        </div>
      </div>
    </div>
  );
};
